import { type BridgeCard, BRIDGE_SUITS, type BridgeSuit, highCardPoints, suitCounts } from './cards.js';
import {
  type BridgeControllers,
  type BridgeSeat,
  type BridgeSide,
  clockwiseAfter,
  partnerOf,
  seatsOf,
  sideOf,
} from './seats.js';

/** A trump suit, or `NT` for no trump. */
export type BridgeTrump = BridgeSuit | 'NT';

/**
 * What the declaring side must achieve. With no auction (this is Mini-Bridge) it follows from the deal.
 * `trump` is null until the declarer chooses it (`chooseTrump`); `suggestedTrump` is what the engine
 * would pick, which the declarer is shown.
 */
export interface BridgeContract {
  readonly declaringSide: BridgeSide;
  readonly declarer: BridgeSeat;
  readonly dummy: BridgeSeat;
  /** Combined high-card points of the declaring side. Private to the engine; never put in a view. */
  readonly declaringPoints: number;
  readonly tricksNeeded: number;
  readonly suggestedTrump: BridgeTrump;
  readonly trump: BridgeTrump | null;
}

/**
 * Tricks the declaring side must take, from its combined points. Calibrated by robot self-play
 * (`scripts/bridge-calibrate.ts`, 20,000 deals) so a good declarer makes the contract about 60% of the
 * time; the usual point-count guidance (26 for game, 33 for a small slam) is a little too easy for
 * single-deal play with no bidding, so each band asks for about one more trick. The declaring side
 * always has at least 20. To be reviewed by someone who teaches bridge.
 */
export function tricksNeededFor(points: number): number {
  if (points >= 37) return 13;
  if (points >= 34) return 12;
  if (points >= 31) return 11;
  if (points >= 27) return 10;
  if (points >= 24) return 9;
  return 8;
}

/**
 * The trump the engine suggests for a pair: their longest combined suit if it has at least 8 cards,
 * otherwise no trump. Ties: more combined high-card points in the suit, then the higher suit.
 */
export function suggestTrump(a: readonly BridgeCard[], b: readonly BridgeCard[]): BridgeTrump {
  const ca = suitCounts(a);
  const cb = suitCounts(b);
  let best: { suit: BridgeSuit; length: number; points: number } | null = null;
  for (const suit of BRIDGE_SUITS) {
    const length = ca[suit] + cb[suit];
    if (length < 8) continue;
    const points = highCardPoints([...a, ...b].filter((c) => c.suit === suit));
    // BRIDGE_SUITS ascend, so `>=` lets a higher suit win a full tie.
    if (!best || length > best.length || (length === best.length && points >= best.points)) {
      best = { suit, length, points };
    }
  }
  return best ? best.suit : 'NT';
}

/**
 * Who declares on the declaring side. A lone human on that side declares (so a single player who
 * wins the auction-less contract always gets to play it). Otherwise (two humans, or none) the
 * stronger hand declares, and a tie goes to the seat that comes first clockwise after the dealer.
 */
export function chooseDeclarer(
  side: BridgeSide,
  hands: Readonly<Record<BridgeSeat, readonly BridgeCard[]>>,
  controllers: BridgeControllers,
  dealer: BridgeSeat,
): BridgeSeat {
  const [first, second] = seatsOf(side);
  const humans = [first, second].filter((s) => controllers[s] === 'human');
  if (humans.length === 1) return humans[0] as BridgeSeat;
  const pa = highCardPoints(hands[first]);
  const pb = highCardPoints(hands[second]);
  if (pa !== pb) return pa > pb ? first : second;
  return clockwiseAfter(dealer).find((s) => s === first || s === second) as BridgeSeat;
}

/** The contract that follows from a deal. The side with more combined points declares; a tie goes to the dealer's side. */
export function buildContract(
  hands: Readonly<Record<BridgeSeat, readonly BridgeCard[]>>,
  dealer: BridgeSeat,
  controllers: BridgeControllers,
): BridgeContract {
  const points: Record<BridgeSide, number> = {
    NS: highCardPoints(hands.N) + highCardPoints(hands.S),
    EW: highCardPoints(hands.E) + highCardPoints(hands.W),
  };
  const declaringSide: BridgeSide =
    points.NS === points.EW ? sideOf(dealer) : points.NS > points.EW ? 'NS' : 'EW';
  const declarer = chooseDeclarer(declaringSide, hands, controllers, dealer);
  const dummy = partnerOf(declarer);
  return {
    declaringSide,
    declarer,
    dummy,
    declaringPoints: points[declaringSide],
    tricksNeeded: tricksNeededFor(points[declaringSide]),
    suggestedTrump: suggestTrump(hands[declarer], hands[dummy]),
    trump: null,
  };
}

